import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import useAuthStore from "../store/authStore";
import { ShieldCheck, Eye, EyeOff, Loader2, ArrowRight, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role !== "admin") {
        await logout();
        toast.error("Access denied. Admin credentials required.");
        return;
      }
      toast.success("Welcome, Admin!");
      if (data.must_change_password) {
        navigate("/force-password-change");
        return;
      }
      navigate("/dashboard");
    } catch {
      toast.error("Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] flex flex-col" data-testid="admin-login-page">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#FF6B00]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-slate-500/5 rounded-full blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2.5 no-underline group" data-testid="admin-login-logo">
          <Logo size="sm" textColor="text-white" />
        </Link>
        <Link
          to="/login"
          className="text-sm text-slate-400 hover:text-white transition-colors no-underline"
          data-testid="admin-login-user-link"
        >
          User Login
        </Link>
      </div>

      {/* Center content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          {/* Admin badge */}
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 bg-[#FF6B00]/10 border border-[#FF6B00]/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <ShieldCheck className="h-8 w-8 text-[#FF6B00]" />
            </div>
          </div>

          <div className="text-center mb-8">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white" data-testid="admin-login-heading">
              Admin Portal
            </h1>
            <p className="text-sm text-slate-400 mt-2">
              Restricted access. Authorized personnel only.
            </p>
          </div>

          {/* Login card */}
          <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-300">Admin Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    placeholder="admin@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-xl bg-white/[0.06] border-white/[0.1] text-white placeholder:text-slate-500 h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                    required
                    data-testid="admin-login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter admin password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-xl bg-white/[0.06] border-white/[0.1] text-white placeholder:text-slate-500 h-12 pl-10 pr-12 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                    required
                    data-testid="admin-login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="admin-login-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold shadow-sm shadow-[#FF6B00]/30 hover:shadow-md hover:shadow-[#FF6B00]/20 transition-all active:scale-[0.98]"
                data-testid="admin-login-submit-button"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ShieldCheck className="h-5 w-5 mr-2" />
                )}
                {loading ? "Authenticating..." : "Access Admin Panel"}
              </Button>
            </form>
          </div>

          <p className="text-xs text-center text-slate-500 mt-6" data-testid="admin-login-security-note">
            This portal is monitored. Unauthorized access attempts are logged.
          </p>
        </div>
      </div>
    </div>
  );
}
